// client/src/pages/DashboardPage.jsx
import { useNavigate } from 'react-router-dom'
import { Car, AlertTriangle, Calendar, ChevronRight, Clock, CheckCircle2, Eye, FileText, Search } from 'lucide-react'
import clsx from 'clsx'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../context/AuthContext'
import { useVehicles, useIssues, useAllSchedules, useQuotes } from '../api/hooks'
import { getScheduleStatus } from '../utils/schedule'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  critical: 'text-red-400 bg-red-500/10 border-red-500/20',
  high:     'text-orange-400 bg-orange-500/10 border-orange-500/20',
  medium:   'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  low:      'text-blue-400 bg-blue-500/10 border-blue-500/20',
}

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-1.5 py-0.5 rounded">
      {registration}
    </span>
  )
}

function StatCard({ label, value, colour, icon: Icon, onClick }) {
  const colours = {
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    red:    'bg-red-500/10    text-red-400    border-red-500/20',
    amber:  'bg-amber-500/10  text-amber-400  border-amber-500/20',
    green:  'bg-green-500/10  text-green-400  border-green-500/20',
  }
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={clsx(
        'text-left p-5 rounded-xl border transition-all',
        colours[colour] || colours.indigo,
        onClick && 'hover:border-[var(--border-strong)] cursor-pointer'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <Icon size={18} />
        {onClick && <ChevronRight size={14} className="opacity-50" />}
      </div>
      <p className="font-display text-3xl font-bold">{value ?? '–'}</p>
      <p className="text-xs font-medium mt-1 opacity-80">{label}</p>
    </Tag>
  )
}

// ─── Schedule alert row ───────────────────────────────────────────────────────

function ScheduleRow({ schedule, navigate }) {
  const { status, daysUntil } = getScheduleStatus(schedule, schedule.current_mileage)

  const label = status === 'overdue'
    ? `${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} overdue`
    : daysUntil != null
      ? `Due in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`
      : 'Due soon'

  const badgeStyle = status === 'overdue'
    ? 'bg-red-500/15 text-red-400 border border-red-500/20'
    : 'bg-amber-500/15 text-amber-400 border border-amber-500/20'

  return (
    <button
      onClick={() => navigate(`/vehicles/${schedule.vehicle_id}`)}
      className="w-full flex items-center gap-3 px-4 py-3 bg-[var(--bg-base)]
                 border border-[var(--border)] rounded-xl hover:border-[var(--border-strong)]
                 transition-all text-left group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {schedule.registration && <Plate registration={schedule.registration} />}
          <span className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
            {schedule.item_name || 'Service'}
          </span>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          {schedule.make} {schedule.model}
        </p>
      </div>
      <span className={clsx('shrink-0 px-2 py-0.5 rounded text-xs font-medium', badgeStyle)}>
        {label}
      </span>
      <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)] group-hover:text-indigo-400" />
    </button>
  )
}

// ─── Quote badge ──────────────────────────────────────────────────────────────

const QUOTE_BADGE = {
  draft:    { label: 'Quote being prepared', cls: 'bg-gray-500/15 text-gray-400 border-gray-500/20' },
  sent:     { label: 'Quote awaiting approval', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
  viewed:   { label: 'Quote awaiting approval', cls: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' },
  approved: { label: 'Quote approved', cls: 'bg-green-500/15 text-green-400 border-green-500/20' },
}

function QuoteBadge({ status, total }) {
  const b = QUOTE_BADGE[status]
  if (!b) return null
  const fmtGBP = v => `£${Number(v || 0).toFixed(2)}`
  return (
    <span className={clsx('shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border', b.cls)}>
      <FileText size={11} />
      {b.label}{total ? ` — ${fmtGBP(total)}` : ''}
    </span>
  )
}

// ─── Issue row ────────────────────────────────────────────────────────────────

function IssueRow({ issue, navigate }) {
  return (
    <button
      onClick={() => navigate(`/issues/${issue.id}`)}
      className="w-full flex items-center gap-3 px-4 py-3 bg-[var(--bg-base)]
                 border border-[var(--border)] rounded-xl hover:border-[var(--border-strong)]
                 transition-all text-left group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
          {issue.title}
        </p>
        {issue.registration && (
          <div className="flex items-center gap-2 mt-0.5">
            <Plate registration={issue.registration} />
          </div>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-2 flex-wrap justify-end">
        {issue.linked_job_investigation && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                           bg-purple-500/15 text-purple-400 border border-purple-500/30">
            <Search size={11} />
            Investigation
          </span>
        )}
        {issue.linked_quote_status && (
          <QuoteBadge status={issue.linked_quote_status} total={issue.linked_quote_total} />
        )}
        {issue.severity && (
          <span className={clsx(
            'px-2 py-0.5 rounded text-xs font-medium border capitalize',
            SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.low
          )}>
            {issue.severity}
          </span>
        )}
      </div>
      <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)] group-hover:text-indigo-400" />
    </button>
  )
}

// ─── Admin/garage welcome ─────────────────────────────────────────────────────

function StaffWelcome({ user, navigate, onCustomerView }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          Welcome, {user?.name?.split(' ')[0] || 'there'}.
        </h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Use the navigation to access your workspace.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {user?.role === 'platform_admin' && (
          <>
            <button onClick={() => navigate('/garage')}
              className="text-left p-5 bg-[var(--bg-surface)] border border-[var(--border)]
                         hover:border-[var(--border-strong)] rounded-xl transition-all group">
              <p className="font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
                Garage Dashboard
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Jobs, quotes, invoices</p>
            </button>
            <button onClick={() => navigate('/admin')}
              className="text-left p-5 bg-[var(--bg-surface)] border border-[var(--border)]
                         hover:border-[var(--border-strong)] rounded-xl transition-all group">
              <p className="font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
                Admin Panel
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Users, garages, audit logs</p>
            </button>
          </>
        )}
        {(user?.role === 'garage_user' || user?.role === 'garage_admin') && (
          <button onClick={() => navigate('/garage')}
            className="text-left p-5 bg-[var(--bg-surface)] border border-[var(--border)]
                       hover:border-[var(--border-strong)] rounded-xl transition-all group">
            <p className="font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
              Go to Garage Dashboard
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Jobs, customers, parts</p>
          </button>
        )}
        <button
          onClick={onCustomerView}
          className="flex items-start gap-4 text-left p-5 bg-indigo-600/5 border border-indigo-500/20
                     hover:border-indigo-500/40 hover:bg-indigo-600/10 rounded-xl transition-all group"
        >
          <Eye size={16} className="mt-0.5 shrink-0 text-indigo-400" />
          <div>
            <p className="font-medium text-indigo-400 group-hover:text-indigo-300 transition-colors">
              My Vehicles &amp; Services
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              View your own vehicles, schedules, and issues
            </p>
          </div>
        </button>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const { user, switchToCustomerView } = useAuth()

  const isCustomer = user?.role === 'customer'

  const { data: vehicles  = [], isLoading: vLoading }  = useVehicles()
  const { data: issues    = [], isLoading: iLoading }  = useIssues()
  const { data: schedules = [], isLoading: sLoading }  = useAllSchedules()
  const { data: allQuotes = [], isLoading: qLoading }  = useQuotes()

  const handleCustomerView = async () => {
    try {
      await switchToCustomerView()
      queryClient.clear()
      navigate('/')
    } catch (err) {
      console.error('Failed to switch to customer view:', err)
    }
  }

  if (!isCustomer) return <StaffWelcome user={user} navigate={navigate} onCustomerView={handleCustomerView} />

  const openIssues = issues.filter(i =>
    !['resolved', 'deferred'].includes(i.status) && i.is_customer_visible !== false
  )

  const alertSchedules = schedules.filter(s => {
    const { status } = getScheduleStatus(s, s.current_mileage)
    return status === 'overdue' || status === 'due-soon'
  }).sort((a, b) => {
    const aStatus = getScheduleStatus(a, a.current_mileage).status
    const bStatus = getScheduleStatus(b, b.current_mileage).status
    if (aStatus === 'overdue' && bStatus !== 'overdue') return -1
    if (bStatus === 'overdue' && aStatus !== 'overdue') return 1
    return (a.next_due_date || '') < (b.next_due_date || '') ? -1 : 1
  })

  const actionableQuotes = allQuotes.filter(q => ['sent', 'viewed'].includes(q.status))
  const loading = vLoading || iLoading || sLoading

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Dashboard</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Welcome back, {user?.name?.split(' ')[0] || 'there'}.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Vehicles"
          value={vLoading ? '–' : vehicles.length}
          colour="indigo"
          icon={Car}
          onClick={() => navigate('/vehicles')}
        />
        <StatCard
          label={alertSchedules.filter(s => getScheduleStatus(s, s.current_mileage).status === 'overdue').length > 0
            ? 'Services Overdue'
            : 'Services Due Soon'}
          value={sLoading ? '–' : alertSchedules.length}
          colour={alertSchedules.some(s => getScheduleStatus(s, s.current_mileage).status === 'overdue') ? 'red' : 'amber'}
          icon={Calendar}
          onClick={() => navigate('/schedule')}
        />
        <StatCard
          label="Open Issues"
          value={iLoading ? '–' : openIssues.length}
          colour={openIssues.some(i => ['high','critical'].includes(i.severity)) ? 'red' : 'amber'}
          icon={AlertTriangle}
          onClick={() => navigate('/issues')}
        />
      </div>

      {/* Quotes requiring action */}
      {(actionableQuotes.length > 0 || qLoading) && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">
              Quotes Requiring Action
            </h2>
            <button
              onClick={() => navigate('/quotes')}
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View all <ChevronRight size={15} />
            </button>
          </div>
          {qLoading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {actionableQuotes.map(q => (
                <button
                  key={q.id}
                  onClick={() => navigate('/quotes')}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-[var(--bg-base)]
                             border border-indigo-500/30 rounded-xl hover:border-indigo-500/60
                             transition-all text-left group"
                >
                  <FileText size={16} className="shrink-0 text-indigo-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
                      {q.quote_number}
                    </p>
                    <p className="text-xs text-[var(--text-muted)] truncate">
                      {q.registration} — {q.make} {q.model}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-[var(--text-primary)]">
                    £{Number(q.total || 0).toFixed(2)}
                  </span>
                  <span className="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/15 text-blue-400">
                    Awaiting your approval
                  </span>
                  <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)] group-hover:text-indigo-400" />
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* No vehicles prompt */}
      {!vLoading && vehicles.length === 0 && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
          <Car size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
          <p className="font-medium text-[var(--text-primary)] mb-1">No vehicles yet</p>
          <p className="text-sm text-[var(--text-muted)] mb-4">
            Add your first vehicle to start tracking maintenance and issues.
          </p>
          <button
            onClick={() => navigate('/vehicles')}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Add a vehicle
          </button>
        </div>
      )}

      {/* Services needing attention */}
      {(alertSchedules.length > 0 || sLoading) && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">
              Services Needing Attention
            </h2>
            <button
              onClick={() => navigate('/schedule')}
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View all <ChevronRight size={15} />
            </button>
          </div>

          {sLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {alertSchedules.slice(0, 5).map(s => (
                <ScheduleRow key={s.id} schedule={s} navigate={navigate} />
              ))}
              {alertSchedules.length > 5 && (
                <button
                  onClick={() => navigate('/schedule')}
                  className="w-full py-2.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  +{alertSchedules.length - 5} more — view all
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* All services up to date */}
      {!sLoading && schedules.length > 0 && alertSchedules.length === 0 && (
        <div className="flex items-center gap-3 px-5 py-4 bg-green-500/10 border border-green-500/20 rounded-xl">
          <CheckCircle2 size={18} className="text-green-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-400">All services up to date</p>
            <p className="text-xs text-green-400/70 mt-0.5">
              No upcoming maintenance due in the next 30 days.
            </p>
          </div>
        </div>
      )}

      {/* Open issues */}
      {(openIssues.length > 0 || iLoading) && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">
              Open Issues
            </h2>
            <button
              onClick={() => navigate('/issues')}
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View all <ChevronRight size={15} />
            </button>
          </div>

          {iLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {openIssues.slice(0, 5).map(issue => (
                <IssueRow key={issue.id} issue={issue} navigate={navigate} />
              ))}
              {openIssues.length > 5 && (
                <button
                  onClick={() => navigate('/issues')}
                  className="w-full py-2.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  +{openIssues.length - 5} more — view all
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* Quick links */}
      {!loading && vehicles.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold text-[var(--text-primary)] mb-3">Quick Links</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'My Vehicles',      path: '/vehicles', icon: Car },
              { label: 'Service Schedule', path: '/schedule', icon: Calendar },
              { label: 'Issues',           path: '/issues',   icon: AlertTriangle },
              { label: 'Service History',  path: '/history',  icon: Clock },
            ].map(({ label, path, icon: Icon }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className="text-left p-4 bg-[var(--bg-surface)] border border-[var(--border)]
                           hover:border-[var(--border-strong)] rounded-xl transition-all group"
              >
                <Icon size={16} className="text-[var(--text-muted)] mb-2" />
                <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
                  {label}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
